import React from 'react';
import { Card, Row, Col, Statistic, Table, List, Tag, Progress } from 'antd';
import { CarOutlined, UserOutlined, DollarOutlined, OrderedListOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const AdminDashboard: React.FC = () => {
  // シミュレート数据
  const overviewData = {
    totalOrders: 1520,
    totalUsers: 8960,
    totalRevenue: 125600,
    totalCars: 350,
    orderGrowth: 12.5,
    userGrowth: 8.3,
    revenueGrowth: 15.2,
    carUtilization: 78.5,
  };

  const recentOrders = [
    { id: '2024010101', user: '田中太郎', car: 'フォルクスワーゲン Polo', amount: 176, status: 'confirmed', time: '10:30' },
    { id: '2024010102', user: '佐藤花子', car: 'ホンダ シビック', amount: 236, status: 'pending', time: '11:15' },
    { id: '2024010103', user: '山田次郎', car: 'トヨタ カムリ', amount: 336, status: 'completed', time: '12:00' },
    { id: '2024010104', user: '鈴木三郎', car: 'メルセデスベンツ C200', amount: 576, status: 'confirmed', time: '13:45' },
  ];

  const popularCars = [
    { model: 'フォルクスワーゲン Polo', orders: 156, utilization: 85 },
    { model: 'ホンダ シビック', orders: 134, utilization: 82 },
    { model: 'トヨタ カムリ', orders: 98, utilization: 75 },
    { model: 'メルセデスベンツ C200', orders: 67, utilization: 68 },
  ];

  const monthlyData = [
    { month: '1月', orders: 120, revenue: 8500 },
    { month: '2月', orders: 132, revenue: 9200 },
    { month: '3月', orders: 101, revenue: 7800 },
    { month: '4月', orders: 134, revenue: 9600 },
    { month: '5月', orders: 90, revenue: 6700 },
    { month: '6月', orders: 230, revenue: 16800 },
    { month: '7月', orders: 210, revenue: 15200 },
    { month: '8月', orders: 156, revenue: 11400 },
    { month: '9月', orders: 187, revenue: 13600 },
    { month: '10月', orders: 198, revenue: 14200 },
    { month: '11月', orders: 165, revenue: 12100 },
    { month: '12月', orders: 201, revenue: 14500 },
  ];

  const ordersByCity = [
    { city: '東京', orders: 450, percentage: 30 },
    { city: '大阪', orders: 380, percentage: 25 },
    { city: '名古屋', orders: 285, percentage: 19 },
    { city: '福岡', orders: 228, percentage: 15 },
    { city: '札幌', orders: 177, percentage: 11 },
  ];

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: 'orange',
      confirmed: 'blue',
      completed: 'green',
      cancelled: 'red',
    };
    return colorMap[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      pending: '承認待ち',
      confirmed: '確認済み',
      completed: '完了',
      cancelled: 'キャンセル',
    };
    return textMap[status] || status;
  };

  // 图表配置暂时注释，因为缺少图表库
  // const lineConfig = {
  //   data: monthlyData,
  //   xField: 'month',
  //   yField: 'orders',
  // };

  const orderColumns = [
    {
      title: '注文番号',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'ユーザー',
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: '車両',
      dataIndex: 'car',
      key: 'car',
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `¥${amount}`,
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '時間',
      dataIndex: 'time',
      key: 'time',
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>ダッシュボード概要</h1>
      
      {/* 概览统计 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="総注文数"
              value={overviewData.totalOrders}
              prefix={<OrderedListOutlined />}
              suffix={
                <span style={{ fontSize: 14, color: '#52c41a' }}>
                  <ArrowUpOutlined /> {overviewData.orderGrowth}%
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="登録ユーザー"
              value={overviewData.totalUsers}
              prefix={<UserOutlined />}
              suffix={
                <span style={{ fontSize: 14, color: '#52c41a' }}>
                  <ArrowUpOutlined /> {overviewData.userGrowth}%
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="総売上"
              value={overviewData.totalRevenue}
              prefix={<DollarOutlined />}
              precision={0}
              suffix={
                <span style={{ fontSize: 14, color: '#52c41a' }}>
                  <ArrowUpOutlined /> {overviewData.revenueGrowth}%
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="車両数"
              value={overviewData.totalCars}
              prefix={<CarOutlined />}
              suffix={
                <span style={{ fontSize: 14, color: '#1890ff' }}>
                  稼働率 {overviewData.carUtilization}%
                </span>
              }
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* 注文趋势 */}
        <Col xs={24} lg={12}>
          <Card title="注文推移" style={{ height: 400 }}>
            <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
              グラフコンポーネント統合待ち
            </div>
          </Card>
        </Col>
        
        {/* 收入趋势 */}
        <Col xs={24} lg={12}>
          <Card title="売上推移" style={{ height: 400 }}>
            <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
              グラフコンポーネント統合待ち
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* 城市分布 */}
        <Col xs={24} lg={8}>
          <Card title="注文都市分布" style={{ height: 400 }}>
            <div style={{ padding: 20 }}>
              {ordersByCity.map((item, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span>{item.city}</span>
                  <span>{item.orders} ({item.percentage}%)</span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        
        {/* 热门车型 */}
        <Col xs={24} lg={8}>
          <Card title="人気車種" style={{ height: 400 }}>
            <List
              dataSource={popularCars}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={item.model}
                    description={`注文数：${item.orders}`}
                  />
                  <div style={{ width: 100 }}>
                    <Progress
                      percent={item.utilization}
                      size="small"
                      status={item.utilization > 80 ? 'success' : 'normal'}
                    />
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        
        {/* 最新注文 */}
        <Col xs={24} lg={8}>
          <Card title="最新注文" style={{ height: 400 }}>
            <Table
              dataSource={recentOrders}
              columns={orderColumns}
              pagination={false}
              size="small"
              scroll={{ y: 280 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;